import { HashRouter, Route, Switch } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import CreateNew from '../create-new/CreateNew';
import SubmenuSidebar from '../../../../global/submenu-sidebar/SubmenuSidebar';
import { TenantIdContext } from '../../../../components/app/App';
import { WorkflowEditor } from '../workflow-editor/WorkflowEditor';
import WorkflowViewer from '../workflow-editor/WorkflowViewer';
import WorkflowTesting from '../testing/WorkflowTesting';
import { getTenantWorkflows } from '../../services/api-calls';
import HistoryViewer from '../history/HistoryViewer';

const MENU_CREATE_NEW = '+ Create New';

const App = () => {
  const tenantId = useContext(TenantIdContext);
  const [tenantWorkflows, setTenantWorkflows] = useState([]);
  const [tenantWorkflowsLoading, setTenantWorkflowsLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const [activePath, setActivePath] = useState('/workflows');
  const history = useHistory();

  const createRouteRenderer = (Component, otherProps, getPathFromProps) => {
    return props => {
      const routePath = getPathFromProps(props);
      return (
        <Component
          {...props}
          {...otherProps}
          setActivePath={() => {
            setActivePath(routePath);
          }}
        />
      );
    };
  };

  useEffect(() => {
    const getWorkflows = () => {
      getTenantWorkflows(tenantId).then(response => {
        const workflows = response?.data || [];
        const newMenuItems = [];
        for (const workflow of workflows) {
          newMenuItems.push({
            title: workflow.workflowName,
            subtitle: workflow.workflowId,
            href: `/workflows/${workflow.workflowId}/edit`,
          });
        }
        newMenuItems.push({
          title: MENU_CREATE_NEW,
          href: '/workflows/create-new',
        });
        setTenantWorkflowsLoading(false);
        setTenantWorkflows(workflows);
        setMenuItems(newMenuItems);
      });
    };
    history.push('/workflows');
    getWorkflows();
  }, [tenantId, history]);

  const workflowCreated = data => {
    const newMenuItems = menuItems.slice(0, -1);
    newMenuItems.push({
      title: data.workflowName,
      subtitle: data.workflowId,
      href: `/workflows/${data.workflowId}/edit`,
    });
    newMenuItems.push(menuItems[menuItems.length - 1]);

    setMenuItems(newMenuItems);
    setTenantWorkflows([...tenantWorkflows, data]);
    setTenantWorkflowsLoading(false);
  };

  const workflowDeleted = deletedWorkflowId => {
    setMenuItems(menuItems.filter(menuItem => menuItem.subtitle !== deletedWorkflowId));
    setTenantWorkflows(tenantWorkflows.filter(tenantWorkflow => tenantWorkflow.workflowId !== deletedWorkflowId));
    setTenantWorkflowsLoading(false);
  };

  const switchComponent = (
    <Switch>
      <Route
        exact
        path="/workflows/:id/edit"
        render={createRouteRenderer(
          WorkflowEditor,
          {
            tenantWorkflows: tenantWorkflows,
            onWorkflowDeleted: workflowDeleted,
          },
          props => `/workflows/${props.match.params.workflowId}/edit`
        )}
      />
      <Route
        exact
        path="/workflows/:id/history"
        render={createRouteRenderer(
          HistoryViewer,
          { tenantWorkflows: tenantWorkflows },
          props => `/workflows/${props.match.params.workflowId}/history`
        )}
      />
      <Route
        exact
        path="/workflows/:id/live"
        render={createRouteRenderer(
          WorkflowViewer,
          { tenantWorkflows: tenantWorkflows },
          props => `/workflows/${props.match.params.workflowId}/live`
        )}
      />
      <Route
        exact
        path="/workflows/:id/test"
        render={createRouteRenderer(
          WorkflowTesting,
          { tenantWorkflows: tenantWorkflows },
          props => `/workflows/${props.match.params.workflowId}/test`
        )}
      />
      <Route
        exact
        path="/workflows/create-new"
        render={createRouteRenderer(
          CreateNew,
          {
            workflows: tenantWorkflows,
            onWorkflowsChange: setTenantWorkflows,
            workflowsLoading: tenantWorkflowsLoading,
            onWorkflowCreateSubmit: () => {
              setTenantWorkflowsLoading(true);
            },
            onWorkflowCreated: workflowCreated,
            onWorkflowCreationFailed: () => {
              setTenantWorkflowsLoading(false);
            },
          },
          props => `/workflows/create-new`
        )}
      />
    </Switch>
  );

  return (
    <HashRouter>
      <div className="d-flex h-100">
        <SubmenuSidebar
          title="Workflows"
          linkItems={menuItems}
          activePath={activePath}
          showLoadingSpinnerAtTitle={true}
          isDataLoading={tenantWorkflowsLoading}
        />
        <div className="h-100 d-flex flex-column p-1 overflow-y-auto flex-grow-1">
          {tenantWorkflows && switchComponent}
        </div>
      </div>
    </HashRouter>
  );
};

export default App;
